/**
 * Block Renderer for Dynamic Pages
 * Renders different content block types
 */

export class BlockRenderer {
  static render(block) {
    const { type, content, settings } = block;
    
    switch (type) {
      case 'text': return this.renderText(content, settings);
      case 'heading': return this.renderHeading(content, settings);
      case 'image': return this.renderImage(content, settings);
      case 'cards': return this.renderCards(content, settings);
      case 'cta': return this.renderCTA(content, settings);
      case 'team': return this.renderTeam(content, settings);
      case 'timeline': return this.renderTimeline(content, settings);
      case 'stats': return this.renderStats(content, settings);
      case 'quote': return this.renderQuote(content, settings);
      case 'faq': return this.renderFAQ(content, settings);
      default: return `<div class="block-unknown">Unknown block: ${type}</div>`;
    }
  }

  static renderText(content, settings) {
    const { html, align = 'left', maxWidth = 'medium' } = content;
    return `
      <div class="block-text align-${align} width-${maxWidth}">
        ${html}
      </div>
    `;
  }

  static renderHeading(content, settings) {
    const { text, level = 2, align = 'left', hasUnderline = false, subtitle = '' } = content;
    const Tag = `h${level}`;
    return `
      <div class="block-heading align-${align} ${hasUnderline ? 'has-underline' : ''}">
        <${Tag}>${text}</${Tag}>
        ${subtitle ? `<p class="heading-subtitle">${subtitle}</p>` : ''}
      </div>
    `;
  }

  static renderImage(content, settings) {
    const { src, alt = '', caption = '', alignment = 'center', width = 'large', borderRadius = 'none' } = content;
    return `
      <figure class="block-image align-${alignment} width-${width} radius-${borderRadius}">
        <img src="${src}" alt="${alt}" loading="lazy">
        ${caption ? `<figcaption>${caption}</figcaption>` : ''}
      </figure>
    `;
  }

  static renderCards(content, settings) {
    const { layout = '3-col', cards = [], animation = 'fade-up' } = content;
    const columns = layout === '2-col' ? 2 : layout === '3-col' ? 3 : 4;
    
    return `
      <div class="block-cards layout-${layout}" data-animation="${animation}">
        <div class="cards-grid" style="--columns: ${columns}">
          ${cards.map((card, i) => `
            <article class="feature-card style-${card.style || 'default'}" style="--delay: ${i * 0.1}s">
              ${card.image ? `<div class="card-image"><img src="${card.image}" alt="${card.title}" loading="lazy"></div>` : ''}
              ${card.icon ? `<div class="card-icon">${card.icon}</div>` : ''}
              <h3 class="card-title">${card.title}</h3>
              <p class="card-description">${card.description}</p>
              ${card.link ? `<a href="${card.link}" class="card-link">${card.linkText || 'Learn More'} →</a>` : ''}
            </article>
          `).join('')}
        </div>
      </div>
    `;
  }

  static renderCTA(content, settings) {
    const { style = 'gradient', title, subtitle, buttons = [], backgroundImage = '', alignment = 'center' } = content;
    return `
      <div class="block-cta style-${style} align-${alignment}" ${backgroundImage ? `style="background-image: url(${backgroundImage})"` : ''}>
        <div class="cta-content">
          <h2 class="cta-title">${title}</h2>
          ${subtitle ? `<p class="cta-subtitle">${subtitle}</p>` : ''}
          <div class="cta-buttons">
            ${buttons.map(btn => `
              <a href="${btn.url}" class="btn btn-${btn.style || 'primary'}">${btn.text}</a>
            `).join('')}
          </div>
        </div>
      </div>
    `;
  }

  static renderTeam(content, settings) {
    const { layout = 'grid', members = [], showSocial = true } = content;
    return `
      <div class="block-team layout-${layout}">
        <div class="team-grid">
          ${members.map(member => `
            <article class="team-member">
              <div class="member-image">
                <img src="${member.image}" alt="${member.name}" loading="lazy">
              </div>
              <h3 class="member-name">${member.name}</h3>
              <p class="member-role">${member.role}</p>
              ${member.bio ? `<p class="member-bio">${member.bio}</p>` : ''}
              ${showSocial && member.social ? `
                <div class="member-social">
                  ${Object.entries(member.social).map(([platform, url]) => `
                    <a href="${url}" target="_blank" rel="noopener" class="social-${platform}">${platform}</a>
                  `).join('')}
                </div>
              ` : ''}
            </article>
          `).join('')}
        </div>
      </div>
    `;
  }

  static renderTimeline(content, settings) {
    const { items = [], layout = 'vertical' } = content;
    return `
      <div class="block-timeline layout-${layout}">
        ${items.map((item, i) => `
          <div class="timeline-item status-${item.status}">
            <div class="timeline-marker">${item.icon || '●'}</div>
            <div class="timeline-content">
              <time class="timeline-date">${item.date}</time>
              <h3 class="timeline-title">${item.title}</h3>
              <p class="timeline-description">${item.description}</p>
            </div>
          </div>
        `).join('')}
      </div>
    `;
  }

  static renderStats(content, settings) {
    const { layout = 'grid', stats = [], style = 'default' } = content;
    return `
      <div class="block-stats layout-${layout} style-${style}">
        <div class="stats-grid">
          ${stats.map(stat => `
            <div class="stat-item">
              <span class="stat-value" data-count="${stat.value.replace(/[^0-9]/g, '')}">${stat.prefix || ''}${stat.value}${stat.suffix || ''}</span>
              <span class="stat-label">${stat.label}</span>
            </div>
          `).join('')}
        </div>
      </div>
    `;
  }

  static renderQuote(content, settings) {
    const { text, author, role, image, style = 'default' } = content;
    return `
      <blockquote class="block-quote style-${style}">
        <p class="quote-text">"${text}"</p>
        <footer class="quote-footer">
          ${image ? `<img src="${image}" alt="${author}" class="quote-image">` : ''}
          <div class="quote-author">
            <cite class="author-name">${author}</cite>
            ${role ? `<span class="author-role">${role}</span>` : ''}
          </div>
        </footer>
      </blockquote>
    `;
  }

  static renderFAQ(content, settings) {
    const { items = [], allowMultiple = false } = content;
    return `
      <div class="block-faq" data-allow-multiple="${allowMultiple}">
        ${items.map((item, i) => `
          <details class="faq-item" ${i === 0 && !allowMultiple ? 'open' : ''}>
            <summary class="faq-question">${item.question}</summary>
            <div class="faq-answer">${item.answer}</div>
          </details>
        `).join('')}
      </div>
    `;
  }
}
